import { Fragment, useEffect, useRef, useState } from "react"
import { displayDestination } from "../lib/urls"
import CustomizePanel from "./CustomizePanel"
import LivePreview from "./LivePreview"
import { BLEED_HERO, FINISH_DRAWER, NARROW_QUERY, PINNED_PREVIEW, RAIL_REST } from "../lib/previewLayout"
import useMediaQuery from "../hooks/useMediaQuery"
import EmailCapture from "./EmailCapture"
import PricingModal from "./PricingModal"
import SaveStatus from "./SaveStatus"

// The marketing hero's LIVE state: once a code exists, the builder claims the
// full hero width and becomes a two-pane workspace — a default-open, two-column
// customise grid on the left and a sticky preview rail (QR + short link +
// downloads + "make another") on the right — led by the anonymous save-to-inbox
// capture panel, which sits above the grid so the pitch to claim the code lands
// before the user starts customising. This replaces the old tall ResultCard
// stack and mirrors the dashboard creator's workspace; the differences are
// hero-only: EmailCapture stays (anonymous users), the free-tier wordmark on
// the rendered code stays, and customise is open by default.
//
// The finish (#316): the rail leads with the code and its downloads, so the
// file is on the first screen, with the two plan notes between them (#330),
// and a Done bar sits at the foot of the design options with the save status
// beside it. Done swaps the workspace for a "You're set" view (downloads,
// change the design, make another). Restyles still save as you go; Done only
// says the person is finished.
//
// All builder state, the debounced live-restyle effect, and bot protection
// live in QrBuilder; this component is presentation + a little local UI state
// (copy feedback, the transient "created" pill).

// "SVG" is not a word these people know, and Word, Canva and Publisher all take
// PNG. Say which file to use.
export const WHICH_FILE_HINT = "PNG for Word, Canva and Publisher. SVG for a print shop."

function pngFor(imageUrl) {
  if (!imageUrl) return null
  return imageUrl.replace(/\.svg(\?.*)?$/, ".png$1")
}

// A link has no spaces, so the browser wraps it wherever the line runs out,
// in the middle of a word. Offer a break after each piece of URL punctuation,
// so a long link wraps between its parts ("example." / "org/mass-times").
function withBreaks(text) {
  return text.split(/([./?&=#_-])/).map((part, i) => (i % 2 ? <Fragment key={i}>{part}<wbr /></Fragment> : part))
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

const ghostBtn =
  "inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded border " +
  "border-[color:var(--color-border)] dark:border-storm text-coal dark:text-cloud text-sm font-semibold " +
  "transition hover:border-voltage hover:text-voltage " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

// The person came for a file, so the PNG download is the one voltage button on
// the card (#265). SVG gets the solid coal fill beside it, and copying the short
// link is the ghost: useful, but not the job.
const primaryBtn =
  "inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-lg bg-voltage text-coal " +
  "text-sm font-semibold shadow-voltage transition hover:bg-voltage-dark " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

const solidBtn =
  "inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-lg bg-coal text-cloud " +
  "dark:bg-cloud dark:text-coal text-sm font-semibold transition hover:opacity-90 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

export default function HeroResult({ result, design, setDesign, restyleError, restyleStatus = "idle", onReset, onUpgrade, isPaid = false, creatorEmail = null, emailSent = undefined, emailStatus = undefined, restored = false }) {
  const [copied, setCopied] = useState(false)
  const [madePill, setMadePill] = useState(false)
  // Done pressed: the workspace gives way to the finished view. "Change the
  // design" brings it back. HeroResult remounts for every new code, so this
  // starts false each time.
  const [finished, setFinished] = useState(false)
  // Below 900px: one column, the pinned preview and the drawer
  // (lib/previewLayout). Switched in JS: the preview must pin inside a box with
  // the options, and on desktop the two are in different columns.
  const narrow = useMediaQuery(NARROW_QUERY)
  const prevToken = useRef(null)
  const sectionRef = useRef(null)

  // Route upgrade CTAs (the wordmark link, shape locks) through the passed
  // handler when there is one (a signed-in mount brings the app's own box);
  // the anonymous marketing mount passes none, so open the in-place pricing
  // modal instead of navigating away to /pricing and abandoning the code.
  // `reason` names the control that was clicked so the modal's headline answers
  // it (#268). Callers pass it explicitly: `onClick={goUpgrade}` would hand it
  // the click event instead.
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState(null)
  const goUpgrade = (reason) => {
    if (onUpgrade) return onUpgrade(reason)
    setUpgradeReason(reason)
    setUpgradeOpen(true)
  }

  // Save-to-inbox capture, shown as a dismissable modal the moment a code goes
  // live so it's a deliberate step rather than something to scroll past — and so
  // the code itself (QR + downloads) is the first thing on screen once it closes.
  // HeroResult unmounts on "Make another" (QrBuilder returns to idle), so a fresh
  // code re-mounts this with captureOpen=true again; restyles keep it mounted and
  // don't reopen it.
  // …unless the email was already taken at create time (the EmailGate path),
  // in which case there is nothing left to capture and the modal would just be
  // asking again for something we have. That arm shows a static strip instead.
  // A code reopened from the "Your recent code" card (`restored`, issue #264)
  // gets neither: it did not just go live, and the address it may already
  // carry never comes back to the browser, so there is nothing true to show.
  const [captureOpen, setCaptureOpen] = useState(!creatorEmail && !restored)
  const [captureShown, setCaptureShown] = useState(false)
  const dialogRef = useRef(null)
  const closeCapture = () => setCaptureOpen(false)

  // Native <dialog> for Esc + focus-trap + focus-restore, mirroring EditCodeModal.
  // Rendered only while open, so the ref is present when this effect runs.
  useEffect(() => {
    if (!captureOpen) return
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    const raf = requestAnimationFrame(() => setCaptureShown(true))
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleCancel = (e) => { e.preventDefault(); closeCapture() } // Esc
    const handleClick = (e) => { if (e.target === dialog) closeCapture() } // scrim
    dialog?.addEventListener("cancel", handleCancel)
    dialog?.addEventListener("click", handleClick)
    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prevOverflow
      dialog?.removeEventListener("cancel", handleCancel)
      dialog?.removeEventListener("click", handleClick)
      setCaptureShown(false)
    }
  }, [captureOpen])

  // Transient "Code created" pill — fires once per freshly created code, not on
  // the restyle re-renders that follow as the user tweaks the design, and not
  // for a code reopened from the recent card, which was created earlier.
  useEffect(() => {
    const token = result?.token || null
    const fresh = token && token !== prevToken.current
    prevToken.current = token
    if (!fresh || restored) return
    setMadePill(true)
    const t = setTimeout(() => setMadePill(false), 3200)
    // Hide it with the timer, so a re-run can never leave the pill up.
    return () => { clearTimeout(t); setMadePill(false) }
  }, [result?.token, restored])

  // Bring the top of the card into view once the code is live. The idle form
  // is short and this workspace is tall, so on a small or zoomed viewport the
  // heading can land above the fold and the first words on screen are an
  // address the person does not know (#263). Waits a frame because the host
  // page reflows the hero grid around the builder in the same tick; does
  // nothing when the heading is already on screen.
  //
  // Held while the save-to-inbox dialog is open: it locks page scroll, and the
  // person cannot see the card behind it. It runs when the dialog closes, which
  // is when the heading has to be on screen.
  //
  // It runs again on Done and on "Change the design": both swap a tall view
  // for a short one, which can leave the person looking at the page below.
  useEffect(() => {
    if (!result?.token || captureOpen) return
    const raf = requestAnimationFrame(() => {
      const el = sectionRef.current
      if (!el) return
      const { top } = el.getBoundingClientRect()
      if (top >= 0 && top <= window.innerHeight / 2) return
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      el.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" })
    })
    return () => cancelAnimationFrame(raf)
  }, [result?.token, captureOpen, finished])

  // The create response carries `redirect_url`; codes loaded from a list use
  // `short_url`. Accept either so the short link populates in both paths.
  const shareUrl = result?.redirect_url || result?.short_url
  const shortDisplay = shareUrl ? shareUrl.replace(/^https?:\/\//, "") : ""
  // What a scan opens: the person's own link, as she reads it.
  const destinationDisplay = displayDestination(result?.destination_url)

  async function handleCopy() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { setCopied(false) }
  }

  // Encode the real short URL in the preview so the module pattern matches the
  // printed code (and the free-tier wordmark renders, since the marketing mount
  // is anonymous). Restyle changes flow through QrBuilder's effect and update
  // result.image_url, keeping downloads in sync with the preview.
  const previewDesign = { ...design, token: result.token }

  // Her own link first: it is the answer to "does this code open my page?".
  // The short link is what the code encodes, so it comes second and says what
  // it is. An unexplained tgo.sh address on its own read as "the code goes to
  // the wrong place" (#263).
  //
  // A hosted contact page (#332) has an address of ours, not hers, so the
  // block names it and opens it, and says the one thing a scanner does that a
  // vCard code wouldn't make them do: one more tap.
  const contactPage = Boolean(result?.contact_page)
  const links = (
    <div className="w-full flex flex-col gap-3">
      {contactPage && (
        <div>
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mb-1.5">Opens</p>
          <div className="px-3 py-2 rounded border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span className="text-sm text-ink dark:text-cloud">Your contact page</span>
            <a
              href={result.destination_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-voltage-deep dark:text-voltage-light underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
            >
              Open your page
            </a>
          </div>
          <p className="mt-1.5 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
            A scan opens your page. One more tap saves your contact.
          </p>
        </div>
      )}
      {destinationDisplay && !contactPage && (
        <div>
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mb-1.5">Opens</p>
          <div className="px-3 py-2 rounded border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight">
            <span className="font-mono text-sm text-ink dark:text-cloud break-words line-clamp-3" title={result.destination_url}>{withBreaks(destinationDisplay)}</span>
          </div>
        </div>
      )}
      <div>
        <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mb-1">Short link inside the code</p>
        <span className="block font-mono text-sm text-ink dark:text-cloud truncate">{shortDisplay}</span>
      </div>
    </div>
  )

  // The person came for a file, so the PNG download is the one voltage button
  // (#265). `row` lays the three out in one line for the finished view.
  const downloads = (row = false) => (
    <div className="w-full flex flex-col gap-2">
      <div className={row ? "flex flex-wrap gap-2" : "flex flex-col gap-2"}>
        <a href={pngFor(result.image_url)} download={`tango-qr-${result.token}.png`} className={primaryBtn + (row ? " px-6" : " w-full")}>
          <DownloadIcon /> Download PNG
        </a>
        <div className={row ? "contents" : "grid grid-cols-2 gap-2"}>
          <a href={result.image_url} download={`tango-qr-${result.token}.svg`} className={solidBtn + (row ? " px-5" : "")} aria-label="Download SVG">
            <DownloadIcon /> SVG
          </a>
          {/* No aria-label: it would hold the button's name at "Copy short
              link" while the visible text changes to "Copied!". The live
              region below tells a screen reader that the copy happened. */}
          <button type="button" onClick={handleCopy} className={ghostBtn + (row ? " px-4" : "")}>
            {copied
              ? <><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg> Copied!</>
              : <><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg> Copy link</>}
          </button>
        </div>
      </div>
      <span className="sr-only" aria-live="polite">{copied ? "Short link copied" : ""}</span>
      <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">
        {WHICH_FILE_HINT}
      </p>
    </div>
  )

  // Disclosures, under the preview and before the downloads, in both views: a
  // person downloads as soon as the button is in view, so what comes with the
  // file is said first. "tangoqr" prints on the flyer, and the code costs
  // nothing as it is (#267); the edit limit is said where the person commits,
  // with "keeps redirecting forever" in the same line so it does not read as
  // an expiry (#261). #316 moved them below the downloads, the links and the
  // short link, out of view on a laptop; they are two short lines, so Download
  // PNG still makes the first screen with them above it (#330). The number is
  // QrCode::SCAN_PAYWALL_THRESHOLD.
  const freeNotes = isPaid ? (
    <p className="w-full text-xs text-[color:var(--color-muted)] dark:text-muted-light">
      Pro: no wordmark, every code stays editable.
    </p>
  ) : (
    <div className="w-full flex flex-col gap-2 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
      <p>
        Free codes carry a small tangoqr mark. The code is free and keeps working.{" "}
        <button
          type="button"
          onClick={() => goUpgrade("wordmark")}
          className="font-semibold text-plasma dark:text-plasma-light underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
        >
          Pro removes the mark
        </button>
      </p>
      <p>Change its link free for its first 50 scans. After that it keeps redirecting forever, and a change needs Pro.</p>
    </div>
  )

  const rail = (
    // Keep the order: preview, plan notes, downloads, links
    // (test/integration/landing_page_test.rb). The narrow layout below places
    // the same pieces in the same order.
    <aside className="col-start-2 row-start-1 sticky top-24 self-start">
      <div className="rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal p-4 flex flex-col items-center gap-4">
        <LivePreview design={previewDesign} maxWidth="max-w-none" />
        {freeNotes}
        {downloads()}
        <div className="w-full pt-4 border-t border-[color:var(--color-border)] dark:border-storm">{links}</div>
      </div>
    </aside>
  )

  const customize = (
    <CustomizePanel
      layout="dashboard"
      open
      design={design}
      setDesign={setDesign}
      error={restyleError}
      existingLogoUrl={result?.logo_url || null}
      canUseShapes={isPaid}
      onUpgrade={() => goUpgrade("shapes")}
    />
  )

  // The finish line (#316). The design options used to end on the Pro note
  // with nothing after it, so a person who had styled the code had no way to
  // say they were finished. Sticky, so it is in view at any point in the
  // options. On a narrow screen it is a drawer on the bottom edge
  // (lib/previewLayout).
  const finishBar = (
    <div
      className={
        narrow
          ? "sticky bottom-0 z-30 " + BLEED_HERO + " " + FINISH_DRAWER
          : "sticky bottom-3 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight px-4 py-3 shadow-md"
      }
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0">
        <SaveStatus status={restyleStatus} />
        <p className={(narrow ? "text-xs leading-snug" : "text-sm") + " text-[color:var(--color-muted)] dark:text-muted-light"}>Changes save as you go.</p>
      </div>
      <button type="button" onClick={() => setFinished(true)} className={primaryBtn + " flex-shrink-0 " + (narrow ? "px-5" : "px-7")}>
        Done <ArrowIcon />
      </button>
    </div>
  )

  // Below 900px, one column: the code pinned to the top inside a box with the
  // options, so it stops where they stop; the drawer after that box, so it
  // pushes the code away rather than sliding over it.
  const workspace = narrow ? (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <div className={BLEED_HERO + " " + PINNED_PREVIEW}>
          <LivePreview design={previewDesign} maxWidth="max-w-none" pinned />
        </div>
        <div className={RAIL_REST + " flex flex-col items-center gap-4"}>
          {freeNotes}
          {downloads()}
          <div className="w-full pt-4 border-t border-[color:var(--color-border)] dark:border-storm">{links}</div>
        </div>
        <div className="min-w-0">{customize}</div>
      </div>
      {finishBar}
    </div>
  ) : (
    <div className="grid grid-cols-[minmax(0,1fr)_300px] min-[1100px]:grid-cols-[minmax(0,1fr)_340px] gap-x-[30px] items-start">
      {rail}
      <div className="min-w-0 col-start-1 row-start-1">
        {customize}
        {finishBar}
      </div>
    </div>
  )

  const finishedView = (
    <div className="flex flex-col min-[760px]:flex-row gap-8 items-center min-[760px]:items-start">
      <div className="w-full max-w-[300px] flex-shrink-0 rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal p-6">
        <LivePreview design={previewDesign} maxWidth="max-w-none" />
      </div>
      {/* The rail's order beside the bigger preview: plan notes, then the
          downloads, then the links (#330). */}
      <div className="flex-1 min-w-0 w-full flex flex-col gap-5">
        {freeNotes}
        {downloads(true)}
        {links}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 border-t border-[color:var(--color-border)] dark:border-storm">
          <button type="button" onClick={() => setFinished(false)} className={ghostBtn + " px-4"}>
            <PencilIcon /> Change the design
          </button>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-coal dark:text-cloud hover:text-voltage-dark dark:hover:text-voltage-light focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            <PlusIcon /> Make another code
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <section ref={sectionRef} className="scroll-mt-4 bg-white dark:bg-midnight rounded-2xl shadow-lg border border-[color:var(--color-border)] dark:border-storm p-6 sm:p-8" aria-label="Your QR code">
      <header className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-extrabold tracking-tight text-xl text-coal dark:text-cloud">
            {finished ? "You're set" : "Your code is live"}
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
            {finished ? "Download it and print it anywhere." : "Download it now, or style it first."}
          </p>
        </div>
        <div className="flex-shrink-0">
          {madePill ? (
            <span role="status" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-voltage-pale dark:bg-voltage/15 text-voltage-dark dark:text-voltage-light text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-voltage" /> Code created
            </span>
          ) : finished && <SaveStatus status={restyleStatus} />}
        </div>
      </header>
      {/* EmailGate path: the address came in with the create, so confirm what
          happened instead of re-asking. Three outcomes, and they are not
          interchangeable — "not_saved" means we declined to store the address
          at all (a binding ceiling), so promising a future sign-in against it
          would be a lie and the resend endpoint would 422. That branch has to
          come first. When a send was merely suppressed (same address inside the
          hour, or the IP over its fan-out budget) the address IS stored and the
          code is still live and editable — we just point at the earlier email
          rather than sending a duplicate. */}
      {creatorEmail && (
        <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-voltage/40 bg-voltage-pale dark:bg-voltage/10 px-4 py-3">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5 text-voltage-deep dark:text-voltage-light">
            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          <p className="text-sm text-coal dark:text-cloud">
            {emailStatus === "not_saved" ? (
              <>
                Your code is live and yours on this browser. We couldn't save{" "}
                <span className="font-mono">{creatorEmail}</span> against it right now —{" "}
                <a
                  href="/login?return_to=/app"
                  className="font-semibold text-voltage-dark dark:text-voltage-light underline underline-offset-2 hover:text-coal dark:hover:text-cloud focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
                >
                  sign in to keep it
                </a>.
              </>
            ) : emailSent === false ? (
              <>
                Saved to <span className="font-mono">{creatorEmail}</span>. We emailed you in the last hour already — check that thread.
              </>
            ) : (
              <>
                Sent to <span className="font-mono">{creatorEmail}</span>. Sign in with that address on any device to edit this code.
              </>
            )}
          </p>
        </div>
      )}

      {captureOpen && (
        <dialog
          ref={dialogRef}
          aria-label="Save this code to your inbox"
          className="p-0 m-auto bg-transparent backdrop:bg-coal/60 backdrop:backdrop-blur-sm overflow-visible"
        >
          <div
            className={
              "w-[min(92vw,480px)] max-h-[90dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud " +
              "rounded-2xl shadow-lg border border-[color:var(--color-border)] dark:border-storm overflow-hidden " +
              "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] " +
              (captureShown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2")
            }
          >
            <div className="flex-none flex justify-end px-3 pt-3">
              <button
                type="button"
                onClick={closeCapture}
                aria-label="Close"
                className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition bg-transparent border-0 p-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto px-6 pb-6 pt-1">
              <EmailCapture token={result.token} layout="modal" onClose={closeCapture} />
            </div>
          </div>
        </dialog>
      )}

      {finished ? finishedView : workspace}

      <PricingModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        claimToken={result.token}
        reason={upgradeReason}
      />
    </section>
  )
}
